import { cariRecCacheKey, carRefCacheKey, casRefCacheKey } from './requestCache';

export const CARI_BUNDLE_SCHEMA_VERSION = 3;

export function isCariBundleAuthError(error) {
  const status = Number(error?.response?.status ?? error?.status ?? 0);
  return status === 401 || status === 403;
}

function numericVersion(value) {
  if (value == null || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function validateCariBundle(bundle, expectedCarRef = null) {
  if (!bundle || typeof bundle !== 'object') return { valid: false, reason: 'missing_bundle' };
  const carRef = Number(bundle.entityId ?? bundle.carRef ?? 0);
  if (!carRef || (expectedCarRef && carRef !== Number(expectedCarRef))) {
    return { valid: false, reason: 'entity_mismatch' };
  }
  const schemaVersion = numericVersion(bundle.schemaVersion);
  if (schemaVersion != null && (schemaVersion < 0 || schemaVersion > CARI_BUNDLE_SCHEMA_VERSION)) {
    return { valid: false, reason: 'unsupported_schema' };
  }
  if (bundle.sourceVersion != null && !['string', 'number'].includes(typeof bundle.sourceVersion)) {
    return { valid: false, reason: 'invalid_source_version' };
  }
  if (Number(bundle.segment?.total ?? 1) > 1) {
    return { valid: false, reason: 'partial_bundle' };
  }
  if (!bundle.carikart || typeof bundle.carikart !== 'object' || Array.isArray(bundle.carikart)) {
    return { valid: false, reason: 'missing_carikart' };
  }
  for (const field of ['cariAramaList', 'satisList', 'islemList', 'paraList', 'paraOzet', 'resimMeta']) {
    if (bundle[field] != null && !Array.isArray(bundle[field])) {
      return { valid: false, reason: `invalid_${field}` };
    }
  }
  return { valid: true, carRef, schemaVersion: schemaVersion ?? 0 };
}

function sourceOrder(bundle) {
  const source = numericVersion(bundle?.sourceVersion);
  if (source != null) return { kind: 'source', value: source };
  const sourceDate = Date.parse(bundle?.sourceVersion || '');
  if (Number.isFinite(sourceDate)) return { kind: 'date', value: sourceDate };
  const updatedAt = Date.parse(bundle?.updatedAt || '');
  return Number.isFinite(updatedAt) ? { kind: 'date', value: updatedAt } : null;
}

export function selectFreshCariBundle(incoming, current, expectedCarRef = null) {
  if (!validateCariBundle(incoming, expectedCarRef).valid) return current || null;
  if (!current || !validateCariBundle(current, expectedCarRef).valid) return incoming;
  const nextOrder = sourceOrder(incoming);
  const currentOrder = sourceOrder(current);
  if (nextOrder && currentOrder && nextOrder.kind === currentOrder.kind && nextOrder.value < currentOrder.value) {
    return current;
  }
  return incoming;
}

function groupByCas(rows, field) {
  const grouped = {};
  for (const row of rows || []) {
    const casRef = Number(row?.[field] ?? row?.[field.toLowerCase()] ?? 0);
    if (!casRef) continue;
    if (!grouped[casRef]) grouped[casRef] = [];
    grouped[casRef].push(row);
  }
  return grouped;
}

export function buildCariBundleCacheEntries(bundle, cid) {
  const validation = validateCariBundle(bundle);
  if (!validation.valid) return [];
  const carRef = validation.carRef;
  const entries = [[cariRecCacheKey(carRef, cid, 'update'), {
    carikart: bundle.carikart,
    cariAramaList: bundle.cariAramaList || [],
  }]];

  if (Array.isArray(bundle.satisList)) entries.push([carRefCacheKey('SATIS_LST', carRef), bundle.satisList]);
  if (Array.isArray(bundle.islemList)) entries.push([carRefCacheKey('ISLEMLIST_BY_CAR', carRef), bundle.islemList]);
  if (Array.isArray(bundle.paraList)) entries.push([carRefCacheKey('PARA_LIST_BY_CAR', carRef), bundle.paraList]);
  if (Array.isArray(bundle.paraOzet)) entries.push([carRefCacheKey('PARA_OZET', carRef), bundle.paraOzet]);
  if (Array.isArray(bundle.resimMeta)) entries.push([carRefCacheKey('RESIM_META', carRef), bundle.resimMeta]);

  const groups = [
    ['ISLEMLIST', bundle.islemByCas || groupByCas(bundle.islemList, 'CI_CAS_REF')],
    ['PARALIST', bundle.paraByCas || groupByCas(bundle.paraList, 'CP_CAS_REF')],
    ['EKIMKANALALIM_LIST', bundle.ekimByCas || groupByCas(bundle.ekimKanalList, 'CE_CAS_REF')],
  ];
  groups.forEach(([prefix, grouped]) => {
    if (!grouped || typeof grouped !== 'object') return;
    Object.entries(grouped).forEach(([casRef, rows]) => {
      if (Array.isArray(rows)) entries.push([casRefCacheKey(prefix, Number(casRef)), rows]);
    });
  });
  return entries;
}

export function shouldMountSalesPanel(openPanels, panelName, activeSatis) {
  if (!openPanels?.[panelName]) return false;
  if (panelName === 'resim') return true;
  return Number(activeSatis?.CAS_REF ?? 0) > 0;
}
